import { Grid, Stack, Typography, Chip, Radio, Tooltip } from '@mui/material';
import React, { FC, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { SCUICollapsiblePaper } from '../../../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { ReturnCommand } from '../../../../../services/command';
import { SCUIReturnProductTable } from '../SCUIReturnProductTable/SCUIReturnProductTable';
import Helper from '../../../../../utils/helper';
import CancelIcon from '@mui/icons-material/Cancel';
import { CRAS1 } from './createReturnActiveStep1.classes';

export const CreateReturnActiveStep1: FC<{
    setIsSellerStore: any;
    setActiveStep: any;
    orderId: any;
    customerId: any;
    shipping: any;
    setSelectedValue: any;
    selectedValue: any;
    setSelectedShipping: any;
    shippingCount: number;
    setSelectedShippingName: any;
}> = ({
    setIsSellerStore,
    setActiveStep,
    orderId,
    customerId,
    shipping,
    setSelectedValue,
    selectedValue,
    setSelectedShipping,
    shippingCount,
    setSelectedShippingName,
}) => {
    const { t } = useTranslation();
    const [datas, setDatas] = React.useState<any>();
    const [returnMessage, setReturnMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const applyShippingSelection = (shippingItem: any, productDatas: any[]): void => {
        if (!shippingItem?.isReturnable || !productDatas?.length) return;

        setIsSellerStore(shippingItem.isSellerStore);
        setSelectedValue(shippingItem.shippingListId);
        const dataWithLostPackage = productDatas.map((item: any) => ({
            ...item,
            isLostPackage: shippingItem.isLostPackage || false,
        }));
        setSelectedShipping(dataWithLostPackage);
        setSelectedShippingName(shippingItem.shippingListNo);
    };

    const listele = async (): Promise<any> => {
        let params: any = {};
        params.orderId = orderId;
        params.customerId = customerId;
        params.shippingListIds = [shipping.shippingListId];
        params.isMarketPlaceReturn = shipping.isSellerStore;
        params.shippingListStatusId = shipping.shippingListStatusId;
        ReturnCommand.PostGetShippingListReturnItems(params).then((res: any) => {
            if (res) {
                const dataWithLostPackage = res.map((item: any) => ({
                    ...item,
                    isLostPackage: shipping.isLostPackage || false,
                }));

                setDatas(dataWithLostPackage);
            } else setDatas([]);
        });
    };

    useEffect(() => {
        listele();
        setReturnMessage(Helper.sanitizeHTML(shipping.returnMessage));
        if (shipping.errorMessage) {
            setErrorMessage(Helper.sanitizeHTML(shipping.errorMessage));
        }
    }, [shipping]);

    // When only one shipping remains (initial load or filter), radio is forced checked
    // without firing onChange — apply the same selection state the checkbox handler would set.
    // Auto-advance only on first selection; if already selected (e.g. user went back to step 1), stay put.
    useEffect(() => {
        if (shippingCount === 1 && shipping?.isReturnable && datas?.length) {
            const shouldAutoAdvance = selectedValue !== shipping.shippingListId;
            applyShippingSelection(shipping, datas);
            if (shouldAutoAdvance) {
                setActiveStep(2);
            }
        }
    }, [shippingCount, datas, shipping]);

    const handleChkBoxChange = (e: any, shippingItem: any): void => {
        applyShippingSelection(shippingItem, datas);
    };

    return (
        <Grid className={CRAS1.root} sx={{ width: '100%' }} container direction="row">
            <Grid
                className={CRAS1.radioCol}
                size={{
                    sm: 1,
                    md: 1,
                    lg: 1,
                    xl: 0.5,
                }}
            >
                <Radio
                    className={CRAS1.radio}
                    sx={{ marginTop: 5 }}
                    disabled={!shipping.isReturnable}
                    value={shipping.shippingListId}
                    onChange={(e) => handleChkBoxChange(e, shipping)}
                    checked={shippingCount === 1 ? true : selectedValue === shipping.shippingListId}
                />
            </Grid>
            <Grid
                className={CRAS1.contentCol}
                size={{
                    sm: 11,
                    md: 11,
                    lg: 11,
                    xl: 11.5,
                }}
            >
                <div className={CRAS1.collapsibleWrap}>
                    <SCUICollapsiblePaper
                        open={shippingCount === 1 ? true : false}
                        changeColorWhenOpen
                        paperText={
                            <Stack className={CRAS1.headerRow} direction="row" spacing={2}>
                                <Typography className={CRAS1.shippingNoText} style={{ marginTop: '0.4rem' }}>
                                    {t('general.shipping')} No: {shipping.shippingListNo}
                                </Typography>
                                {shipping.errorMessage ? (
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '16px' }}>{t('general.productNotReturnable')}</div>
                                        }
                                    >
                                        <div className={CRAS1.errorTooltipWrap}>
                                            <div
                                                className={CRAS1.errorBox}
                                                style={{
                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                                    borderRadius: '0 0 5px 5px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    padding: '0.3rem',
                                                    gap: '0.4rem',
                                                }}
                                            >
                                                <CancelIcon
                                                    className={CRAS1.errorIcon}
                                                    style={{ color: 'red', marginTop: '0.4rem' }}
                                                />
                                                <div
                                                    className={CRAS1.errorText}
                                                    style={{ fontSize: '16px', color: 'red' }}
                                                >
                                                    {parse(DOMPurify.sanitize(errorMessage))}
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                ) : shipping.isReturnable ? (
                                    <Chip
                                        className={CRAS1.sellerChip}
                                        style={{ marginTop: '0.2rem' }}
                                        variant="outlined"
                                        size="small"
                                        label={shipping.sellerName}
                                        title={shipping.sellerName}
                                    />
                                ) : (
                                    <Tooltip
                                        title={
                                            <div style={{ fontSize: '16px' }}>{t('general.productNotReturnable')}</div>
                                        }
                                    >
                                        <div className={CRAS1.notReturnableTooltipWrap}>
                                            <div
                                                className={CRAS1.notReturnableBox}
                                                style={{
                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                                                    borderRadius: '15px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    padding: '0.4rem',
                                                    gap: '0.4rem',
                                                    backgroundColor: 'red',
                                                }}
                                            >
                                                <CancelIcon
                                                    className={CRAS1.notReturnableIcon}
                                                    style={{ color: 'white', marginTop: '0.4rem' }}
                                                />
                                                <div
                                                    className={CRAS1.notReturnableText}
                                                    style={{ fontSize: '16px', color: 'white' }}
                                                >
                                                    {parse(DOMPurify.sanitize(returnMessage))}
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                )}
                            </Stack>
                        }
                    >
                        <div className={CRAS1.productsWrap}>
                            {datas &&
                                datas.map((product: any, index: number) => (
                                    <div className={CRAS1.productRowWrap} key={index}>
                                        <SCUIReturnProductTable data={product} type={'show'} />
                                    </div>
                                ))}
                        </div>
                    </SCUICollapsiblePaper>
                </div>
            </Grid>
        </Grid>
    );
};
